<template>
  <div class="app-container">
    <!-- 搜索区域 -->
    <el-form :model="queryParams" ref="queryForm" :inline="true" v-show="showSearch" label-width="68px" size="mini">
      <el-form-item label="客户名称" prop="customerName">
        <el-input v-model="queryParams.customerName" placeholder="请输入客户名称" clearable style="width: 200px" @keyup.enter.native="handleSearch" />
        <CustomerDetails v-model="queryParams.customerName" />
      </el-form-item>
      <el-form-item label="小区名称" prop="projectName">
        <el-input v-model="queryParams.projectName" placeholder="请输入项目名称" clearable style="width: 200px" @keyup.enter.native="handleSearch" />
      </el-form-item>
      <el-form-item label="签约日期">
        <el-date-picker v-model="dateRange" style="width: 240px" value-format="yyyy-MM-dd" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期"
                        @change="handleSearch"></el-date-picker>
      </el-form-item>
      <el-form-item>
        <!-- 使用自定义工具栏按钮组件 -->
        <PermissionMethodToolbar :url="searchButtonUrl" :id="'customer-search'" @handleSearch="handleSearch" @handleReset="handleReset" />
      </el-form-item>
    </el-form>

    <!-- 操作按钮区域 -->
    <el-row :gutter="10" class="mb8">
      <el-col :span="1.5">
        <!-- 使用自定义工具栏按钮组件 -->
        <PermissionMethodToolbar :url="toolbarUrl" :id="'customer-toolbar'" @handleAdd="handleAdd" @handlePriceScheme="handlePriceScheme" />
      </el-col>
      <right-toolbar :showSearch.sync="showSearch"></right-toolbar>
    </el-row>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="customerList" @selection-change="handleSelectionChange" border>
      <el-table-column type="selection" width="55" align="center" />
      <el-table-column label="客户编号" align="center" prop="customerCode" />
      <el-table-column label="客户名称" align="center" prop="customerName" :show-overflow-tooltip="true" />
      <el-table-column label="联系人" align="center" prop="contactPerson" />
      <el-table-column label="联系电话" align="center" prop="contactPhone" />
      <el-table-column label="使用单位" align="center" prop="usageUnit" :show-overflow-tooltip="true" />
      <el-table-column label="小区名称" align="center" prop="projectName" :show-overflow-tooltip="true" />
      <el-table-column label="现场维保联系人" align="center" prop="maintenanceContact" :show-overflow-tooltip="true" />
      <el-table-column label="现场维保联系人电话" align="center" prop="maintenancePhone" :show-overflow-tooltip="true" />
      <el-table-column label="签约日期" align="center" prop="signDate" width="100">
        <template slot-scope="scope">
          <span>{{ scope.row.signDate }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" class-name="small-padding fixed-width" fixed="right" width="200">
        <template slot-scope="scope">
          <!-- 使用自定义行按钮组件 -->
          <PermissionMethodRow :url="rowButtonUrl" :id="scope.row.id" @handleUpdate="handleUpdate" @handleDelete="handleDelete" @handlePriceScheme="handlePriceScheme" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <pagination v-show="total > 0" :total="total" :page.sync="queryParams.pageNum" :limit.sync="queryParams.pageSize" />

    <!-- 价格方案选择弹窗 -->
    <el-dialog title="价格方案选择" :visible.sync="priceSchemeVisible" width="500px" append-to-body destroy-on-close>
      <div class="price-scheme-container">
        <el-radio-group v-model="selectedScheme" @change="handleSchemeChange">
          <el-radio :label="1">
            <div class="scheme-item">
              <div class="scheme-title">方案1：标准价</div>
              <div class="scheme-desc">按标准价格计算，不进行任何折扣或调整</div>
            </div>
          </el-radio>
          <el-radio :label="2">
            <div class="scheme-item">
              <div class="scheme-title">方案2：标准价折扣</div>
              <div class="scheme-desc">在标准价的前提下，选择统一折扣或每层折扣</div>
              <div v-if="selectedScheme === 2" class="scheme-options">
                <el-radio-group v-model="discountType" size="mini">
                  <el-radio :label="1">统一折扣</el-radio>
                  <el-radio :label="2">每层折扣</el-radio>
                </el-radio-group>
                <div v-if="discountType === 1" class="discount-input">
                  <span>折扣率：</span>
                  <el-input-number v-model="uniformDiscount" :min="1" :max="100" :precision="1" size="mini" style="width: 120px"></el-input-number>
                  <span>%</span>
                </div>
              </div>
            </div>
          </el-radio>
          <el-radio :label="3">
            <div class="scheme-item">
              <div class="scheme-title">方案3：折扣后抹零</div>
              <div class="scheme-desc">在标准价折扣的前提下，对最终价格进行抹零处理</div>
              <div v-if="selectedScheme === 3" class="scheme-options">
                <div class="discount-input">
                  <span>折扣率：</span>
                  <el-input-number v-model="scheme3Discount" :min="1" :max="100" :precision="1" size="mini" style="width: 120px"></el-input-number>
                  <span>%</span>
                </div>
                <div class="discount-input">
                  <span>抹零到：</span>
                  <el-select v-model="roundingType" size="mini" style="width: 120px">
                    <el-option label="元" :value="1"></el-option>
                    <el-option label="十元" :value="10"></el-option>
                    <el-option label="百元" :value="100"></el-option>
                  </el-select>
                </div>
              </div>
            </div>
          </el-radio>
          <el-radio :label="4">
            <div class="scheme-item">
              <div class="scheme-title">方案4：包干价</div>
              <div class="scheme-desc">不按标准价计算，直接指定总价</div>
              <div v-if="selectedScheme === 4" class="scheme-options">
                <div class="discount-input">
                  <span>总价：</span>
                  <el-input-number v-model="fixedPrice" :min="0" :precision="2" size="mini" style="width: 150px"></el-input-number>
                  <span>元</span>
                </div>
              </div>
            </div>
          </el-radio>
        </el-radio-group>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="priceSchemeVisible = false" size="mini">取 消</el-button>
        <el-button type="primary" @click="confirmPriceScheme" size="mini">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 新增/修改客户信息弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="customerFormVisible" width="700px" append-to-body destroy-on-close>
      <el-form ref="customerForm" :model="customerForm" :rules="rules" label-width="100px" size="mini">
        <el-row>
          <el-col :span="12">
            <el-form-item label="客户编号" prop="customerCode">
              <el-input v-model="customerForm.customerCode" placeholder="请输入客户编号" :disabled="dialogType === 'edit'" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="客户名称" prop="customerName">
              <el-input v-model="customerForm.customerName" placeholder="请输入客户名称" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="12">
            <el-form-item label="联系人" prop="contactPerson">
              <el-input v-model="customerForm.contactPerson" placeholder="请输入联系人姓名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="contactPhone">
              <el-input v-model="customerForm.contactPhone" placeholder="请输入联系电话" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="12">
            <el-form-item label="使用单位" prop="usageUnit">
              <el-input v-model="customerForm.usageUnit" placeholder="请输入使用单位" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="小区名称" prop="projectName">
              <el-input v-model="customerForm.projectName" placeholder="请输入项目名称" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="12">
            <el-form-item label="维保联系人" prop="maintenanceContact">
              <el-input v-model="customerForm.maintenanceContact" placeholder="请输入现场维保联系人" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="维保联系电话" prop="maintenancePhone">
              <el-input v-model="customerForm.maintenancePhone" placeholder="请输入维保联系人电话" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="12">
            <el-form-item label="签约日期" prop="signDate">
              <el-date-picker v-model="customerForm.signDate" type="date" placeholder="选择签约日期" value-format="yyyy-MM-dd" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <el-form-item label="备注" prop="remark">
              <el-input v-model="customerForm.remark" type="textarea" placeholder="请输入备注信息" :rows="3" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="customerFormVisible = false" size="mini">取 消</el-button>
        <el-button type="primary" @click="submitCustomerForm" size="mini">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'Customer',
  data () {
    return {
      // 遮罩层
      loading: false,
      // 选中数组
      ids: [],
      // 非单个禁用
      single: true,
      // 非多个禁用
      multiple: true,
      // 显示搜索条件
      showSearch: true,
      // 总条数
      total: 15,
      // 客户表格数据
      customerList: [
        {
          id: 1,
          customerCode: 'KH20230001',
          customerName: '北京建筑工程有限公司',
          contactPerson: '张三',
          contactPhone: '13812345678',
          usageUnit: '北京市政府',
          projectName: '北京市政府办公楼改造工程',
          maintenanceContact: '李四',
          maintenancePhone: '13987654321',
          signDate: '2023-01-15'
        },
        {
          id: 2,
          customerCode: 'KH20230002',
          customerName: '上海市政建设集团',
          contactPerson: '王五',
          contactPhone: '13698765432',
          usageUnit: '上海市交通局',
          projectName: '上海地铁18号线工程',
          maintenanceContact: '赵六',
          maintenancePhone: '13567891234',
          signDate: '2023-02-20'
        },
        {
          id: 3,
          customerCode: 'KH20230003',
          customerName: '广州建设发展有限公司',
          contactPerson: '刘七',
          contactPhone: '13512345678',
          usageUnit: '广州市教育局',
          projectName: '广州市第一中学新校区建设',
          maintenanceContact: '陈八',
          maintenancePhone: '13876543210',
          signDate: '2023-03-05'
        },
        {
          id: 4,
          customerCode: 'KH20230004',
          customerName: '深圳市建筑工程集团',
          contactPerson: '钱九',
          contactPhone: '13923456789',
          usageUnit: '深圳市科技园',
          projectName: '深圳湾科技创新中心',
          maintenanceContact: '孙十',
          maintenancePhone: '13612345678',
          signDate: '2023-03-18'
        },
        {
          id: 5,
          customerCode: 'KH20230005',
          customerName: '杭州建设工程有限公司',
          contactPerson: '周一',
          contactPhone: '13712345678',
          usageUnit: '杭州市西湖区管委会',
          projectName: '西湖景区改造提升工程',
          maintenanceContact: '吴二',
          maintenancePhone: '13887654321',
          signDate: '2023-04-10'
        },
        {
          id: 6,
          customerCode: 'KH20230006',
          customerName: '成都建工集团',
          contactPerson: '郑三',
          contactPhone: '13612345678',
          usageUnit: '成都市高新区',
          projectName: '成都高新技术产业开发区基础设施建设',
          maintenanceContact: '王四',
          maintenancePhone: '13587654321',
          signDate: '2023-04-25'
        },
        {
          id: 7,
          customerCode: 'KH20230007',
          customerName: '重庆市政建设有限公司',
          contactPerson: '冯五',
          contactPhone: '13712345678',
          usageUnit: '重庆市轨道交通集团',
          projectName: '重庆轨道交通5号线延伸工程',
          maintenanceContact: '陈六',
          maintenancePhone: '13876543210',
          signDate: '2023-05-08'
        },
        {
          id: 8,
          customerCode: 'KH20230008',
          customerName: '武汉建筑工程公司',
          contactPerson: '朱七',
          contactPhone: '13812345678',
          usageUnit: '武汉市政府',
          projectName: '武汉长江大桥维修加固工程',
          maintenanceContact: '秦八',
          maintenancePhone: '13698765432',
          signDate: '2023-05-20'
        },
        {
          id: 9,
          customerCode: 'KH20230009',
          customerName: '南京市政工程有限公司',
          contactPerson: '沈九',
          contactPhone: '13912345678',
          usageUnit: '南京市交通局',
          projectName: '南京地铁4号线工程',
          maintenanceContact: '范十',
          maintenancePhone: '13876543210',
          signDate: '2023-06-05'
        },
        {
          id: 10,
          customerCode: 'KH20230010',
          customerName: '西安建筑集团',
          contactPerson: '韩一',
          contactPhone: '13812345678',
          usageUnit: '西安市文化旅游局',
          projectName: '西安古城墙修缮工程',
          maintenanceContact: '魏二',
          maintenancePhone: '13987654321',
          signDate: '2023-06-18'
        }
      ],
      // 日期范围
      dateRange: [],
      // 查询参数
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        customerName: undefined,
        projectName: undefined
      },
      // 价格方案弹窗相关
      priceSchemeVisible: false,
      selectedScheme: 1,
      currentCustomer: null,
      // 方案2相关
      discountType: 1,
      uniformDiscount: 95.0,
      // 方案3相关
      scheme3Discount: 95.0,
      roundingType: 10,
      // 方案4相关
      fixedPrice: 10000.0,

      // 客户表单弹窗相关
      customerFormVisible: false,
      dialogTitle: '新增客户',
      dialogType: 'add', // add或edit
      customerForm: {
        id: undefined,
        customerCode: '',
        customerName: '',
        contactPerson: '',
        contactPhone: '',
        usageUnit: '',
        projectName: '',
        maintenanceContact: '',
        maintenancePhone: '',
        signDate: '',
        remark: ''
      },
      // 表单验证规则
      rules: {
        customerCode: [{ required: true, message: '客户编号不能为空', trigger: 'blur' }],
        customerName: [{ required: true, message: '客户名称不能为空', trigger: 'blur' }],
        contactPerson: [{ required: true, message: '联系人不能为空', trigger: 'blur' }],
        contactPhone: [
          { required: true, message: '联系电话不能为空', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        maintenancePhone: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ]
      }
    }
  },
  created () {
    // 模拟加载数据
    this.loading = true
    setTimeout(() => {
      this.loading = false
    }, 500)
  },
  methods: {
    /** 选中行数据 */
    handleSelectionChange (selection) {
      this.ids = selection.map(item => item.id)
      this.single = selection.length !== 1
      this.multiple = !selection.length
    },
    // 以下方法仅为模拟，不实现具体逻辑
    handleSearch () {
      this.queryParams.pageNum = 1
      this.getList()
    },
    handleReset () {
      this.dateRange = []
      this.resetForm('queryForm')
      this.handleSearch()
    },
    handleAdd () {
      this.dialogTitle = '新增客户'
      this.dialogType = 'add'
      this.resetCustomerForm()
      // 生成一个新的客户编号
      const date = new Date()
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const random = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, '0')
      this.customerForm.customerCode = `KH${year}${month}${random}`
      this.customerFormVisible = true
    },
    handleUpdate (row) {
      this.dialogTitle = '修改客户'
      this.dialogType = 'edit'
      this.resetCustomerForm()
      // 复制当前行数据到表单
      Object.assign(this.customerForm, row)
      this.customerFormVisible = true
    },
    handleDelete (row) {
      this.$message.info(`您点击了删除按钮，客户ID：${row.id}`)
    },
    handlePriceScheme (row) {
      this.currentCustomer = row
      this.priceSchemeVisible = true
      this.$message.info(`为客户 ${row.customerName} 选择价格方案`)
    },
    // 价格方案相关方法
    handleSchemeChange (value) {
      // 切换方案时的处理逻辑
      console.log('选择了方案:', value)
    },
    confirmPriceScheme () {
      // 根据不同方案获取价格信息
      let priceInfo = ''

      switch (this.selectedScheme) {
        case 1:
          priceInfo = '标准价'
          break
        case 2:
          priceInfo =
            this.discountType === 1
              ? `标准价 + 统一折扣${this.uniformDiscount}%`
              : '标准价 + 每层折扣'
          break
        case 3:
          priceInfo = `标准价 + 折扣${this.scheme3Discount}% + 抹零到${this.roundingType === 1 ? '元' : this.roundingType === 10 ? '十元' : '百元'
            }`
          break
        case 4:
          priceInfo = `包干价 ${this.fixedPrice}元`
          break
      }

      this.$message.success(
        `已为客户 ${this.currentCustomer.customerName} 设置价格方案：${priceInfo}`
      )
      this.priceSchemeVisible = false
    },

    // 重置表单
    resetCustomerForm () {
      if (this.$refs.customerForm) {
        this.$refs.customerForm.resetFields()
      }
      this.customerForm = {
        id: undefined,
        customerCode: '',
        customerName: '',
        contactPerson: '',
        contactPhone: '',
        usageUnit: '',
        projectName: '',
        maintenanceContact: '',
        maintenancePhone: '',
        signDate: '',
        remark: ''
      }
    },

    // 提交客户表单
    submitCustomerForm () {
      this.$refs.customerForm.validate(valid => {
        if (valid) {
          const isAdd = this.dialogType === 'add'
          const message = isAdd ? '新增成功' : '修改成功'
          const title = isAdd ? '新增客户' : '修改客户'

          // 这里只是模拟，实际应该调用API
          setTimeout(() => {
            this.$message.success(`${title}：${this.customerForm.customerName} ${message}`)
            this.customerFormVisible = false

            // 如果是新增，模拟添加到列表
            if (isAdd) {
              const newCustomer = { ...this.customerForm }
              newCustomer.id = this.customerList.length + 1
              this.customerList.unshift(newCustomer)
              this.total += 1
            } else {
              // 如果是修改，更新列表中的数据
              const index = this.customerList.findIndex(item => item.id === this.customerForm.id)
              if (index !== -1) {
                this.customerList.splice(index, 1, { ...this.customerForm })
              }
            }
          }, 300)
        }
      })
    }
  }
}
</script>

<style scoped>
.el-table .warning-row {
  background: oldlace;
}
.el-table .success-row {
  background: #f0f9eb;
}
.mb8 {
  margin-bottom: 8px;
}
.price-scheme-container {
  padding: 10px;
}
.scheme-item {
  margin-bottom: 15px;
  padding: 10px;
  border-radius: 4px;
  transition: all 0.3s;
}
.scheme-title {
  font-weight: bold;
  margin-bottom: 5px;
}
.scheme-desc {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
}
.scheme-options {
  background-color: #f8f8f8;
  padding: 10px;
  margin-top: 8px;
  border-radius: 4px;
}
.discount-input {
  display: flex;
  align-items: center;
  margin-top: 10px;
}
.discount-input span {
  margin-right: 5px;
}
::v-deep .el-table__body-wrapper {
  z-index: 2;
}
</style>
